// Frontend API service for logs operations
import { getAuthHeaders, handleAuthError } from './authUtils';

// Treat REACT_APP_API_URL as the origin (scheme+host+port). Always prefix paths with /api
const API_ORIGIN = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_BASE_URL = `${API_ORIGIN}/api`;

class LogsApiService {
  // Get all logs for a service (excluding summary logs)
  async getLogs(serviceName, page = 1, limit = 10, search = '', signal) {
    try {
      const query = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        excludeLevel: 'summary'
      });
      if (search) query.set('search', search);
      const response = await fetch(`${API_BASE_URL}/logs/${serviceName}?${query.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return { logs: [], pagination: {} };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return { logs: [], pagination: {} };
    }
  }

  // Get only error logs for a service (using unified method)
  async getErrorLogs(serviceName, page = 1, limit = 10, search = '', signal) {
    try {
      const query = new URLSearchParams({
        level: 'error',
        page: String(page),
        limit: String(limit)
      });
      if (search) query.set('search', search);
      const response = await fetch(`${API_BASE_URL}/logs/${serviceName}?${query.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include',
        signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return { logs: [], pagination: {} };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return { logs: [], pagination: {} };
    }
  }

  // Clear logs for a service
  async clearLogs(serviceName) {
    try {
      const response = await fetch(`${API_BASE_URL}/logs/${serviceName}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get summary logs for a service (for sync status)
  async getSummaryLogs(service) {
    try {
      const response = await fetch(`${API_BASE_URL}/logs/${service}?level=summary&limit=1`, {
        method: 'GET',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return { summaryLog: null };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Find the summary log from the logs array
      // Backend already filters level=summary and sorts by updatedAt desc; just take most recent
      const summaryLog = data.logs && data.logs.length > 0 ? data.logs[0] : null;
      
      return { summaryLog };
    } catch (error) {
      return { summaryLog: null };
    }
  }
}

export const logsApi = new LogsApiService();
